import { Component } from '@theme/component';
import { observerElement } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A progress bar component that animates when it enters the viewport.
 *
 */
class ProgressBar extends Component {
  #observer = null;
  /** @type {Element | null} - Cached section/dialog the variant listener is attached to. */
  #variantUpdateTarget = null;

  connectedCallback() {
    super.connectedCallback();

    this.#init();

    // Listen for variant updates to reinitialize after morph
    this.#variantUpdateTarget = this.closest('.shopify-section, dialog');
    this.#variantUpdateTarget?.addEventListener(ThemeEvents.variantUpdate, this.#handleProgressBarUpdate);

    document.addEventListener(ThemeEvents.progressBarUpdate, this.#handleProgressBarUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantUpdateTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#handleProgressBarUpdate);
    document.removeEventListener(ThemeEvents.progressBarUpdate, this.#handleProgressBarUpdate);
    this.#disconnectObserver();
  }

  /**
   * Disconnects the observer if it exists
   */
  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Handles variant update events to reinitialize after DOM morph
   */
  #handleProgressBarUpdate = () => {
    this.classList.remove('initialized');
    this.style.removeProperty('--progress');
    this.#disconnectObserver();
    this.#init();
  }

  /**
   * Calculates and sets the progress value
   */
  #calculateAndSetProgress() {
    const value = parseInt(this.getAttribute('data-value')) || 0;
    const max = parseInt(this.getAttribute('data-max')) || 100;
    const progress = (value * 100) / max;

    this.style.setProperty('--progress', `${Math.min(progress, 100)}%`);
    this.classList.add('initialized');
  }

  /**
   * Initializes the progress bar by setting up observer
   */
  #init() {
    if (this.classList.contains('initialized')) return;

    this.#observer = observerElement({
      element: this,
      callback: () => this.#calculateAndSetProgress(),
      threshold: 0
    });
  }
}

if (!customElements.get('progress-bar')) {
  customElements.define('progress-bar', ProgressBar);
}

